/* v6.35.5 — Recent Runs table for the BP Content-Fit Audit.
 *
 * Polls GET /api/bp-auditor/runs?limit=5 every 30s. Shows status chip with
 * v6.35.2 `watchdog_stale` phase support (distinct red "killed by watchdog"
 * chip) and a Resume button for any failed run with results[] history.
 *
 * Resume button → POST /api/bp-auditor/run with {resume_from_run_id} so the
 * worker skips already-audited scenes.
 *
 * v6.35.11 — Inline error/forensic inspector. Any failed or watchdog row
 * gets a chevron toggle that expands a second row showing the raw
 * `error` / `failure_reason` / `phase` plus the forensic snapshot fields
 * (last_current_op, last_completed_op, heartbeat_at, started_at,
 * failed_at). Closes the 'why did this run fail?' loop without needing
 * Mongo or Sentry access.
 *
 * v6.35.12 — Copy-forensics-as-JSON button inside the expanded panel.
 * One click copies a tidy {run_id, status, phase, error, failure_reason,
 * + all forensic fields} JSON blob to the clipboard so the operator
 * can paste it directly into Sentry, Slack, or a postmortem doc.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, RotateCcw, ChevronDown, ChevronRight, Clipboard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const statusBadge = (r) => {
  // v6.35.2 — watchdog_stale takes precedence over generic "failed".
  if (r.phase === 'watchdog_stale') {
    return (
      <Badge
        className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]"
        data-testid="bp-audit-recent-runs-watchdog-chip"
        title={r.failure_reason || 'auto-cancelled by stale-heartbeat watchdog'}
      >
        ⏱ watchdog
      </Badge>
    );
  }
  const s = r.status;
  if (s === 'complete') {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">complete</Badge>;
  }
  if (s === 'failed') {
    return <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]">failed</Badge>;
  }
  if (s === 'running') {
    return <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">running</Badge>;
  }
  return <Badge variant="outline" className="text-[10px]">{s || '—'}</Badge>;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return iso.slice(0, 19).replace('T', ' ');
  } catch {
    return iso;
  }
};

// v6.35.11 — A run is "inspectable" (chevron visible) when it carries
// any forensic signal worth surfacing: a non-empty error message, the
// v6.35.2 watchdog phase, or a failed status. complete/running rows
// stay clean — operators don't need to expand healthy rows.
const isInspectable = (r) =>
  !!r?.error ||
  !!r?.failure_reason ||
  r?.phase === 'watchdog_stale' ||
  r?.status === 'failed';

// v6.35.12 — Compose the JSON blob copied to clipboard when an operator
// hits the "Copy forensics" button. Keep the shape stable so downstream
// consumers (Sentry issue bodies, Slack snippets, RCA pastes) can rely
// on the field set. progress flattened to {done, total} for readability.
const buildForensicsPayload = (r) => ({
  run_id: r?.run_id || null,
  status: r?.status || null,
  phase: r?.phase || null,
  error: r?.error || null,
  failure_reason: r?.failure_reason || null,
  progress: {
    done: r?.progress?.done ?? null,
    total: r?.progress?.total ?? null,
  },
  last_current_op: r?.last_current_op || null,
  last_completed_op: r?.last_completed_op || null,
  last_completed_op_at: r?.last_completed_op_at || null,
  heartbeat_at: r?.heartbeat_at || null,
  started_at: r?.started_at || r?.at || null,
  failed_at: r?.failed_at || null,
  completed_at: r?.completed_at || null,
  by: r?.by || null,
});

const BPAuditRecentRunsTable = ({ onRunStarted }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumingRunId, setResumingRunId] = useState(null);
  // v6.35.11 — which run's forensic row is currently expanded (only one
  // at a time to keep the table compact).
  const [expandedRunId, setExpandedRunId] = useState(null);

  // v6.35.12 — Copy the forensic payload of a single run to clipboard
  // as JSON. Uses navigator.clipboard with a graceful textarea fallback
  // for environments where the Clipboard API is gated (e.g. some
  // private-tab contexts). Always shows a toast so the operator knows
  // whether the copy actually happened.
  const handleCopyForensics = async (r) => {
    const payload = buildForensicsPayload(r);
    const text = JSON.stringify(payload, null, 2);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Forensics copied to clipboard');
    } catch {
      toast.error('Copy failed — clipboard not available');
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/bp-auditor/runs?limit=5`,
        authCfg(),
      );
      setRuns(data?.runs || []);
    } catch (_) {
      // Silent — non-critical companion panel.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const doResume = async (run) => {
    setResumingRunId(run.run_id);
    try {
      const { data } = await axios.post(
        `${API}/bp-auditor/run`,
        { resume_from_run_id: run.run_id },
        authCfg(),
      );
      const skipped = data?.resumed_scene_count ?? (run.progress?.done || 0);
      toast.success(
        `Resume started — skipping ${skipped} already-audited scenes.`,
        { duration: 6000 },
      );
      if (onRunStarted) onRunStarted();
      await load();
    } catch (e) {
      toast.error(`Resume failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setResumingRunId(null);
    }
  };

  if (loading) {
    return null;
  }
  if (!runs.length) {
    return null;
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4"
      data-testid="bp-audit-recent-runs-history"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <History size={14} />
          Recent Audit Runs
          <span className="text-gray-400 font-normal text-xs">· last {runs.length}</span>
        </div>
        <div className="text-[10px] text-gray-400">auto-refresh every 30s</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1 pr-2 font-medium">Run</th>
              <th className="py-1 pr-2 font-medium">Status</th>
              <th className="py-1 pr-2 font-medium">Progress</th>
              <th className="py-1 pr-2 font-medium">Started</th>
              <th className="py-1 pr-2 font-medium">By</th>
              <th className="py-1 font-medium" />
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const done = r.progress?.done ?? 0;
              const total = r.progress?.total ?? 0;
              const resumable =
                r.status === 'failed' &&
                done > 0 &&
                total > 0 &&
                done < total;
              const inspectable = isInspectable(r);
              const expanded = expandedRunId === r.run_id;
              return (
                <React.Fragment key={r.run_id}>
                  <tr
                    className="border-b border-gray-100 last:border-0"
                    data-testid="bp-audit-recent-runs-row"
                    data-run-id={r.run_id}
                    data-phase={r.phase || ''}
                  >
                    <td className="py-1.5 pr-2 font-mono text-[10px] text-gray-700">
                      <span className="inline-flex items-center gap-1">
                        {inspectable ? (
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedRunId(expanded ? null : r.run_id)
                            }
                            className="text-gray-500 hover:text-gray-800"
                            aria-label={
                              expanded ? 'Hide error details' : 'Show error details'
                            }
                            aria-expanded={expanded}
                            data-testid="bp-audit-error-toggle"
                            data-target-run-id={r.run_id}
                            title={expanded ? 'Hide error details' : 'Show error details'}
                          >
                            {expanded ? (
                              <ChevronDown size={12} />
                            ) : (
                              <ChevronRight size={12} />
                            )}
                          </button>
                        ) : (
                          <span className="inline-block w-3" />
                        )}
                        {r.run_id}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2">{statusBadge(r)}</td>
                    <td className="py-1.5 pr-2 text-gray-700">
                      {done}/{total}
                    </td>
                    <td className="py-1.5 pr-2 text-gray-500">
                      {fmtTime(r.started_at || r.at)}
                    </td>
                    <td className="py-1.5 pr-2 text-gray-500 truncate max-w-[180px]">
                      {r.by || '—'}
                    </td>
                    <td className="py-1.5">
                      {resumable && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] px-2"
                          onClick={() => doResume(r)}
                          disabled={resumingRunId === r.run_id}
                          data-testid="bp-audit-resume-btn"
                          data-target-run-id={r.run_id}
                          title={`Resume — skip the ${done} scenes already audited in this run.`}
                        >
                          {resumingRunId === r.run_id ? (
                            'Resuming…'
                          ) : (
                            <>
                              <RotateCcw size={10} className="mr-1" /> Resume
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expanded && (
                    <tr
                      className="bg-gray-50 border-b border-gray-100"
                      data-testid="bp-audit-error-detail-row"
                      data-target-run-id={r.run_id}
                    >
                      <td colSpan={6} className="py-2 px-3">
                        <div className="space-y-2 text-[11px] text-gray-700">
                          {(r.failure_reason || r.error) && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                                {r.failure_reason ? 'Failure reason' : 'Error'}
                              </div>
                              <pre
                                className="whitespace-pre-wrap break-words font-mono text-[11px] text-rose-800 bg-rose-50 border border-rose-100 rounded p-2"
                                data-testid="bp-audit-error-message"
                              >
                                {r.failure_reason || r.error}
                              </pre>
                            </div>
                          )}
                          <div
                            className="grid grid-cols-2 gap-x-4 gap-y-1"
                            data-testid="bp-audit-error-forensics"
                          >
                            <div>
                              <span className="text-gray-500">phase:</span>{' '}
                              <span className="font-mono">{r.phase || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">last_current_op:</span>{' '}
                              <span className="font-mono">{r.last_current_op || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">last_completed_op:</span>{' '}
                              <span className="font-mono">{r.last_completed_op || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">last_completed_op_at:</span>{' '}
                              <span className="font-mono">{fmtTime(r.last_completed_op_at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">heartbeat_at:</span>{' '}
                              <span className="font-mono">{fmtTime(r.heartbeat_at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">started_at:</span>{' '}
                              <span className="font-mono">{fmtTime(r.started_at || r.at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">failed_at:</span>{' '}
                              <span className="font-mono">{fmtTime(r.failed_at)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">completed_at:</span>{' '}
                              <span className="font-mono">{fmtTime(r.completed_at)}</span>
                            </div>
                          </div>
                          {!r.failure_reason && !r.error && (
                            <div className="text-gray-500 italic">
                              No error message captured — the worker died
                              before writing one. Forensic fields above
                              show what the last heartbeat saw.
                            </div>
                          )}
                          {/* v6.35.12 — Copy-to-clipboard for postmortems. */}
                          <div className="pt-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px] px-2"
                              onClick={() => handleCopyForensics(r)}
                              data-testid="bp-audit-copy-forensics-btn"
                              data-target-run-id={r.run_id}
                              title="Copy run_id + error + forensic fields as JSON — paste into Sentry, Slack, or a postmortem doc."
                            >
                              <Clipboard size={10} className="mr-1" />
                              Copy forensics
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Watchdog hint footer */}
      {runs.some((r) => r.phase === 'watchdog_stale') && (
        <div className="text-[10px] text-orange-700 mt-2 flex items-start gap-1">
          <span>⏱</span>
          <span>
            <strong>watchdog</strong> = run was auto-cancelled by the stale-heartbeat
            watchdog (v6.35.2). The worker died without writing a heartbeat for
            10+ min. Click <strong>Resume</strong> to pick up where it left off.
          </span>
        </div>
      )}
    </div>
  );
};

export default BPAuditRecentRunsTable;
